import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Truck, ArrowRight, MapPin, CurrencyDollar, ChatCircleDots, Handshake, Path,
  CheckCircle, ShieldCheck, Lightning, Eye, Scales, Star, XCircle, CaretDown,
  Clock, SealCheck, ChartLineUp, MagnifyingGlass, Package, CircleNotch, PaperPlaneTilt
} from "@phosphor-icons/react";
import { dateTimeLabel, formatTracking } from "../lib/format";

const TRACK_STATUS = {
  ADJUDICADO: "Transportista asignado",
  TRANSPORTISTA_EN_CAMINO: "Transportista en camino",
  EN_ORIGEN: "En el punto de retiro",
  CARGA_RECOGIDA: "Carga recogida",
  EN_TRASLADO: "En traslado",
  ENTREGADO: "Entregado",
  COMPLETADO: "Completado",
  CANCELADO: "Cancelado",
};

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/56feac90-6b6f-4efe-b6c8-6fff264c6455/images/64c0d12acd3afed55fc30f6cb0430a2e935a3f904d85cc15ea93e3b5c736d7df.jpeg";
const FLEET_IMG = "https://static.prod-images.emergentagent.com/jobs/56feac90-6b6f-4efe-b6c8-6fff264c6455/images/d064682d72da4840e13519809fd1cee23c2abb011858d26ec28ee7c94c172c28.jpeg";
const MAP_IMG = "https://static.prod-images.emergentagent.com/jobs/56feac90-6b6f-4efe-b6c8-6fff264c6455/images/6d00ede40d793204987af1e9d474b1af5ceb4b9ebd0a73676a0449670d439dbe.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const STEPS = [
  { icon: MapPin, title: "Publica", text: "Cuéntanos qué necesitas transportar, desde dónde y hacia dónde." },
  { icon: CurrencyDollar, title: "Pon tu precio", text: "Dinos cuánto estás dispuesto a pagar. Tú tienes el control." },
  { icon: ChatCircleDots, title: "Recibe propuestas", text: "Transportistas acreditados aceptan tu precio o te contraofertan." },
  { icon: Handshake, title: "Elige", text: "Compara reputación y precio, y selecciona al que más te convenga." },
  { icon: Path, title: "Transporta", text: "Sigue el servicio en tiempo real, estado por estado, hasta la entrega." },
];

const PROBLEMS = [
  "Precios opacos y sobrecobros de última hora",
  "Llamar a mil números sin saber a quién contratar",
  "Transportistas informales y sin respaldo",
  "Cero visibilidad de dónde va tu carga",
];

const SOLUTIONS = [
  { icon: Scales, title: "Tú pones el precio", text: "Publicas cuánto quieres pagar. Los transportistas compiten por tu flete: aceptan o contraofertan." },
  { icon: SealCheck, title: "Solo acreditados", text: "Cada transportista pasa por un proceso de acreditación con documentos y vehículos validados por Frets." },
  { icon: Eye, title: "Seguimiento transparente", text: "Timeline en vivo: asignado, en camino, carga recogida, en traslado y entregado." },
  { icon: Star, title: "Reputación real", text: "Calificaciones por puntualidad, cuidado de la carga y comunicación tras cada servicio." },
];

const STATS = [
  { value: "100%", label: "Transportistas acreditados" },
  { value: "0", label: "Comisiones ocultas" },
  { value: "5", label: "Pasos para publicar" },
  { value: "24/7", label: "Publica cuando quieras" },
];

const FAQ = [
  { q: "¿Cómo se define el precio?", a: "Tú propones el precio que estás dispuesto a pagar. Los transportistas acreditados pueden aceptarlo tal cual o enviarte una contraoferta. Tú eliges la propuesta que más te convenga." },
  { q: "¿Quiénes son los transportistas?", a: "Todos pasan por un proceso de acreditación donde revisamos sus datos, vehículos y documentación (licencia, permiso de circulación, revisión técnica, etc.). Solo los aprobados pueden tomar servicios." },
  { q: "¿Puedo hacer seguimiento de mi carga?", a: "Sí. Cada servicio tiene un timeline en tiempo real que muestra el avance: transportista asignado, en camino, en origen, carga recogida, en traslado y entregado." },
  { q: "¿Cuánto cuesta usar Frets?", a: "Publicar un flete y recibir propuestas es gratis. Tú acuerdas el precio directamente con el transportista." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8F9FA] overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/frets-logo.png" alt="Frets Transporte" className="h-9 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <a href="#problema" className="px-4 py-2 rounded-full text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors">El problema</a>
            <a href="#solucion" className="px-4 py-2 rounded-full text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors">Cómo funciona</a>
            <a href="#transportistas" className="px-4 py-2 rounded-full text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors">Transportistas</a>
            <a href="#rastreo" className="px-4 py-2 rounded-full text-sm font-semibold text-[#FF5A36] hover:bg-[#FFF4F1] transition-colors">Rastrear carga</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login" className="px-4 py-2 text-sm font-semibold hover:text-[#FF5A36] transition-colors">Ingresar</Link>
            <Link to="/register" data-testid="landing-register" className="px-5 py-2.5 rounded-full bg-[#0F1115] text-white text-sm font-semibold hover:bg-[#1A1D24] transition-colors active:scale-95">Registrarme</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 flets-grain opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-20 md:pt-20 md:pb-28 grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36] bg-[#FFF4F1] border border-[#FFD9CF] rounded-full px-3.5 py-1.5 mb-5">
              <Lightning size={14} weight="fill" /> Fácil · Rápido · A tu precio
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[1.03]">
              El flete que necesitas,<br /><span className="text-[#FF5A36]">al precio que tú decides.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-zinc-600 leading-relaxed max-w-xl">
              Frets es el marketplace donde publicas tu carga, propones cuánto pagar y los transportistas
              <span className="text-[#0F1115] font-semibold"> acreditados compiten</span> por llevarla. Sin llamadas, sin sobrecobros, con seguimiento en vivo.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register?role=CLIENT" data-testid="cta-cotizar"
                className="h-14 px-8 rounded-full bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 shadow-lg shadow-[#FF5A36]/25">
                Cotizar mi flete <ArrowRight size={20} weight="bold" />
              </Link>
              <Link to="/register?role=CARRIER" data-testid="cta-transportar"
                className="h-14 px-8 rounded-full bg-white border border-zinc-300 font-bold flex items-center justify-center hover:border-[#0F1115] transition-colors active:scale-95">
                Quiero transportar
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm text-zinc-500">
              <span className="flex items-center gap-1.5"><ShieldCheck size={18} weight="fill" className="text-emerald-500" /> Transportistas acreditados</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={18} weight="fill" className="text-emerald-500" /> Sin comisiones ocultas</span>
            </div>
          </motion.div>

          {/* Interactive hero visual */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="relative">
            <div className="rounded-3xl overflow-hidden border border-zinc-200 bg-white shadow-2xl">
              <img src={HERO_IMG} alt="Van de reparto moderna de Frets" className="w-full h-[340px] md:h-[440px] object-cover" />
            </div>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
              className="absolute -top-4 -left-3 sm:-left-6 bg-white rounded-2xl border border-zinc-200 shadow-xl px-4 py-3">
              <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Precio ofrecido</span>
              <div className="font-display text-2xl font-black text-[#0F1115]">$65.000</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="absolute -bottom-5 right-2 sm:right-6 bg-white rounded-2xl border border-zinc-200 shadow-xl px-4 py-3 w-56">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">3 propuestas</span>
                <span className="text-xs text-emerald-600 font-bold flex items-center gap-1"><CheckCircle size={13} weight="fill" /> en vivo</span>
              </div>
              <div className="mt-2 space-y-1.5">
                {[["Transportista A", "Acepta $65.000", "text-emerald-600"], ["Transportista B", "Propone $72.000", "text-[#FF5A36]"]].map(([n, p, c]) => (
                  <div key={n} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-zinc-500"><Star size={11} weight="fill" className="text-[#FF5A36]" /> {n}</span>
                    <span className={`font-semibold ${c}`}>{p}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#0F1115] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="text-center md:text-left">
              <div className="font-display text-4xl font-black text-[#FF5A36]">{s.value}</div>
              <div className="text-sm text-zinc-400 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Tracking */}
      <section id="rastreo" className="bg-white border-b border-zinc-200 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36] mb-3"><Package size={16} weight="fill" /> Seguimiento de carga</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Rastrea tu traslado</h2>
          <p className="mt-3 text-zinc-600 text-lg">Ingresa el <span className="font-semibold text-[#0F1115]">número de traslado</span> (12 dígitos) y sigue tu carga en tiempo real. Sin necesidad de iniciar sesión.</p>
          <TrackWidget />
        </div>
      </section>

      {/* Problem */}
      <section id="problema" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36] mb-4">El problema</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Contratar un flete hoy es un dolor de cabeza.</h2>
          <p className="mt-4 text-lg text-zinc-600">Mudarte o mover carga significa perseguir contactos, negociar a ciegas y cruzar los dedos para que llegue todo bien.</p>
          <ul className="mt-8 space-y-3">
            {PROBLEMS.map((p, i) => (
              <motion.li key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="flex items-start gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3.5">
                <XCircle size={22} weight="fill" className="text-red-500 shrink-0" />
                <span className="font-medium text-zinc-700">{p}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
          className="rounded-3xl overflow-hidden border border-zinc-200 shadow-xl">
          <img src={MAP_IMG} alt="Ruta de transporte" className="w-full h-[380px] object-cover" />
        </motion.div>
      </section>

      {/* Solution */}
      <section id="solucion" className="bg-white border-y border-zinc-200 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36] mb-4">La solución</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Un marketplace donde tú tienes el control.</h2>
            <p className="mt-4 text-lg text-zinc-600">Frets convierte el caos en un proceso claro, transparente y a tu favor.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {SOLUTIONS.map((s, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="rounded-2xl border border-zinc-200 p-6 bg-[#F8F9FA] hover:-translate-y-1.5 hover:border-[#FF5A36] transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#0F1115] flex items-center justify-center mb-4">
                  <s.icon size={24} weight="bold" className="text-[#FF5A36]" />
                </span>
                <h3 className="font-semibold text-lg mb-1.5">{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>

          {/* How it works steps */}
          <div className="mt-20">
            <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">¿Cómo funciona?</h3>
            <p className="text-zinc-600 mb-10">Cinco pasos simples, de principio a fin.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {STEPS.map((s, i) => (
                <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="relative rounded-2xl border border-zinc-200 p-6 hover:-translate-y-1 transition-transform bg-white">
                  <span className="w-11 h-11 rounded-xl bg-[#FF5A36] flex items-center justify-center mb-4">
                    <s.icon size={22} weight="bold" className="text-white" />
                  </span>
                  <div className="text-xs font-bold text-[#FF5A36] mb-1">Paso 0{i + 1}</div>
                  <h4 className="font-semibold text-lg mb-1">{s.title}</h4>
                  <p className="text-sm text-zinc-600 leading-relaxed">{s.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Carriers */}
      <section id="transportistas" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="rounded-3xl overflow-hidden border border-zinc-200 shadow-xl order-2 lg:order-1">
          <img src={FLEET_IMG} alt="Flota moderna de transportistas Frets" className="w-full h-[400px] object-cover" />
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className="order-1 lg:order-2">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36] mb-4">¿Eres transportista?</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Encuentra servicios cerca de ti y decide cuáles tomar.</h2>
          <p className="mt-4 text-zinc-600 text-lg">Acredítate una vez y recibe oportunidades compatibles con tus vehículos y zonas. Tú eliges qué fletes aceptar y a qué precio.</p>
          <ul className="mt-6 space-y-3">
            {[
              [Clock, "Oportunidades en tiempo real cerca de ti"],
              [Scales, "Acepta el precio o envía tu contraoferta"],
              [ChartLineUp, "Construye tu reputación y aumenta tus ingresos"],
            ].map(([Icon, t], i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#FFF4F1] flex items-center justify-center"><Icon size={18} weight="bold" className="text-[#FF5A36]" /></span>
                <span className="font-medium text-zinc-700">{t}</span>
              </li>
            ))}
          </ul>
          <Link to="/register?role=CARRIER" data-testid="cta-registro-transportista"
            className="mt-8 inline-flex h-14 px-8 rounded-full bg-[#0F1115] text-white font-bold items-center gap-2 hover:bg-[#1A1D24] transition-colors active:scale-95">
            Registrarme como transportista <ArrowRight size={20} weight="bold" />
          </Link>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-zinc-200 py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-10 text-center">Preguntas frecuentes</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => <FaqItem key={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="relative rounded-3xl bg-[#0F1115] text-white p-10 md:p-16 overflow-hidden flets-grain">
          <div className="relative z-10 max-w-2xl">
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter leading-tight">Sé parte de la comunidad<br />que se mueve.</h2>
            <p className="mt-4 text-zinc-400 text-lg">Miles de cargas y transportistas conectados. Publica tu primer flete en menos de un minuto, o acredítate y empieza a mover carga hoy.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register?role=CLIENT" className="h-14 px-8 rounded-full bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95">
                Cotizar mi flete <ArrowRight size={20} weight="bold" />
              </Link>
              <Link to="/register?role=CARRIER" className="h-14 px-8 rounded-full bg-white/10 border border-white/20 text-white font-bold flex items-center justify-center hover:bg-white/20 transition-colors active:scale-95">
                Quiero transportar
              </Link>
            </div>
          </div>
          <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[#FF5A36]/20 blur-3xl" />
        </motion.div>
      </section>

      <section id="contacto" className="bg-[#F8F9FA] border-t border-zinc-200 py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Contacto</span>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">¿Hablamos?</h2>
            <p className="mt-3 text-base text-zinc-600">Cuéntanos qué necesitas transportar o cómo quieres sumarte a la comunidad. Te respondemos a la brevedad.</p>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="bg-[#0F1115] text-white py-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/frets-logo.png" alt="Frets Transporte" className="h-9 w-auto brightness-0 invert" />
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-zinc-400">
            <Link to="/legal/privacidad" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacidad</Link>
            <Link to="/legal/terminos" className="hover:text-white transition-colors" data-testid="footer-terms">Términos</Link>
            <Link to="/legal/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">Cookies</Link>
            <a href="#contacto" className="hover:text-white transition-colors" data-testid="footer-contact">Contacto</a>
          </div>
          <p className="text-sm text-zinc-400">© {new Date().getFullYear()} DoSoft</p>
        </div>
      </footer>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !form.email.includes("@") || form.message.trim().length < 5) {
      toast.error("Completa tu nombre, un email válido y un mensaje.");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error();
      setSent(true);
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("No pudimos enviar tu mensaje. Intenta nuevamente."); }
    setLoading(false);
  };

  if (sent) return (
    <div className="mt-8 rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="contact-success">
      <CheckCircle size={40} weight="fill" className="text-emerald-600 mx-auto" />
      <h3 className="font-display text-xl font-bold mt-3">¡Gracias por escribirnos!</h3>
      <p className="text-sm text-zinc-600 mt-1">Recibimos tu mensaje y te responderemos a la brevedad.</p>
      <button onClick={() => setSent(false)} className="mt-4 text-sm font-semibold text-[#FF5A36] hover:underline" data-testid="contact-another">Enviar otro mensaje</button>
    </div>
  );

  return (
    <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-zinc-200 shadow-sm p-6 sm:p-8 space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <CField label="Nombre" testid="contact-name" value={form.name} onChange={set("name")} required />
        <CField label="Email" testid="contact-email" type="email" value={form.email} onChange={set("email")} required />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <CField label="Teléfono (opcional)" testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+56 9 ..." />
        <CField label="Asunto" testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="Quiero cotizar / Ser transportista..." />
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">Mensaje</label>
        <textarea data-testid="contact-message" rows={4} value={form.message} onChange={set("message")} required
          placeholder="Cuéntanos en qué te ayudamos"
          className="mt-1 w-full px-4 py-3 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] resize-none" />
      </div>
      <button data-testid="contact-submit" disabled={loading}
        className="w-full h-13 py-3.5 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
        {loading ? <CircleNotch size={20} className="animate-spin" /> : <>Enviar mensaje <PaperPlaneTilt size={18} weight="bold" /></>}
      </button>
    </form>
  );
}

function CField({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">{label}</label>
      <input data-testid={testid} {...props}
        className="mt-1 w-full h-12 px-4 rounded-xl border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] focus:border-transparent" />
    </div>
  );
}

function TrackWidget() {
  const [num, setNum] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const search = async () => {
    const clean = num.replace(/\D/g, "");
    if (clean.length < 6) { setError("Ingresa un número de traslado válido."); setResult(null); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await fetch(`${BACKEND}/api/services/track/${clean}`);
      if (!r.ok) setError("No encontramos ese número de traslado. Revísalo e intenta de nuevo.");
      else setResult(await r.json());
    } catch { setError("Error de conexión. Intenta nuevamente."); }
    setLoading(false);
  };

  const history = result?.statusHistory || [];
  const current = history.length ? history[history.length - 1].status : result?.status;

  return (
    <div className="mt-8 max-w-2xl mx-auto text-left" data-testid="track-widget">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input data-testid="track-input" value={num} onChange={(e) => setNum(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Ej: 4437 4373 5004" inputMode="numeric"
            className="w-full h-14 pl-12 pr-4 rounded-full border border-zinc-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A36] font-mono tracking-wide" />
        </div>
        <button onClick={search} disabled={loading} data-testid="track-btn"
          className="h-14 px-8 rounded-full bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
          {loading ? <CircleNotch size={20} className="animate-spin" /> : <>Rastrear <ArrowRight size={18} weight="bold" /></>}
        </button>
      </div>

      {error && <p className="mt-4 text-sm font-semibold text-red-500 text-center" data-testid="track-error">{error}</p>}

      {result && (
        <div className="mt-6 rounded-2xl border border-zinc-200 bg-[#F8F9FA] p-6" data-testid="track-result">
          <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-zinc-200">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">N° Traslado</div>
              <div className="font-mono font-bold text-lg">{formatTracking(result.trackingNumber)}</div>
            </div>
            <span className="text-sm font-bold px-4 py-1.5 rounded-full bg-[#FF5A36] text-white">{TRACK_STATUS[current] || current}</span>
          </div>
          <div className="flex items-center gap-2 mt-4 font-semibold">
            <MapPin size={18} className="text-[#FF5A36]" /> {result.origin?.city || "—"} → {result.destination?.city || "—"}
          </div>
          <div className="text-sm text-zinc-500 mt-1">{result.category} · Transportista: {result.carrier?.name || "—"}</div>

          {history.length > 0 && (
            <ol className="mt-5 space-y-3" data-testid="track-timeline">
              {history.map((h, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle size={20} weight="fill" className={i === history.length - 1 ? "text-[#FF5A36]" : "text-emerald-500"} />
                  <div>
                    <div className="text-sm font-semibold">{TRACK_STATUS[h.status] || h.status}</div>
                    {h.at && <div className="text-xs text-zinc-400">{dateTimeLabel(h.at)}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-zinc-200 bg-[#F8F9FA] overflow-hidden" data-testid="faq-item">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="font-semibold">{q}</span>
        <CaretDown size={20} className={`text-[#FF5A36] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
        <p className="px-5 pb-5 text-zinc-600 leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
}
